
import { ViewEmployee } from '@xeetr/types/models'
import { weekdaysSelections } from '@xeetr/types/utils'
import { format, isAfter, parse, roundToNearestMinutes } from 'date-fns'
import { Trash2 } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'

import FloatingLabel from '../../../inputs/FloatingLabel'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { MultiSelect } from '@/components/ui/multi-select'
import { TimePicker } from '@/components/ui/time-picker'
import { cn } from '@/lib/utils'


interface Props {
  workingHourIndex: number
  onRemove: () => void
}

const EmployeeWorkingHourCard = ({ workingHourIndex, onRemove }: Props) => {
  const { control } = useFormContext<ViewEmployee>()

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex justify-end">
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => onRemove()}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <Controller
          name={`workingHours.${workingHourIndex}.weekdays`}
          control={control}
          render={({ field, fieldState }) => (
            <FloatingLabel label="Wochentage" className="md:col-span-2">
              <MultiSelect
                className={cn('md:w-full', {
                  'p-invalid': fieldState.error,
                })}
                value={field.value?.map((v) => v.toString())}
                onValueChange={(selectedValues) => {
                  const changed = weekdaysSelections
                    .map((weekDay) => weekDay.value)
                    .filter((weekDay) => selectedValues.includes(weekDay.toString()))
                  field.onChange(changed)
                }}
                options={weekdaysSelections.map((w) => ({
                  value: w.value.toString(),
                  label: w.weekday,
                }))}
                placeholder="Wähle Wochentage"
              />
            </FloatingLabel>
          )}
        />
        <Controller
          control={control}
          name={`workingHours.${workingHourIndex}.from`}
          render={({ field: fromField }) => (
            <Controller
              control={control}
              name={`workingHours.${workingHourIndex}.to`}
              rules={{
                validate: (value) => {
                  if (
                    value &&
                    fromField.value &&
                    !isAfter(
                      parse(value as string, 'HH:mm', new Date()),
                      parse(fromField.value as string, 'HH:mm', new Date())
                    )
                  ) {
                    return 'Arbeitsende muss nach Arbeitsbeginn enden!'
                  }
                },
              }}
              render={({ field: toField, fieldState: { error } }) => (
                <div className="mt-2 grid grid-cols-2 gap-5">
                  <FloatingLabel label="Arbeitsbeginn">
                    <TimePicker
                      value={
                        fromField.value && fromField.value !== ''
                          ? parse(fromField.value as string, 'HH:mm', new Date())
                          : null
                      }
                      stepHour={1}
                      stepMinute={15}
                      onSelect={(e) =>
                        e.value &&
                        fromField.onChange(
                          format(roundToNearestMinutes(e.value, { nearestTo: 15 }), 'HH:mm')
                        )
                      }
                    />
                  </FloatingLabel>
                  <FloatingLabel label="Arbeitsende">
                    <TimePicker
                      className={cn({ invalid: error })}
                      value={
                        toField.value && toField.value !== ''
                          ? parse(toField.value as string, 'HH:mm', new Date())
                          : null
                      }
                      stepHour={1}
                      stepMinute={15}
                      onSelect={(e) => {
                        if (e.value)
                          return toField.onChange(
                            format(roundToNearestMinutes(e.value, { nearestTo: 15 }), 'HH:mm')
                          )
                      }}
                    />
                  </FloatingLabel>
                  {error && <p className="col-span-2 text-red-500">{error.message}</p>}
                </div>
              )}
            />
          )}
        />
      </CardContent>
    </Card>
  )
}

export default EmployeeWorkingHourCard
